import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Pressable } from '@cbhq/cds-web/system';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useUpdateBaseL3 } from ':cloud/queries/Base/useUpdateBaseL3';
import { useGetPaymentMethod } from ':cloud/queries/BillingQueries/useGetPaymentMethod';
import { QueryKey } from ':cloud/queries/types';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { BaseL3Chain } from ':cloud/types/ts_types';
import { getCardMedia } from ':cloud/utils/billing';
import { truncateMiddle } from ':cloud/utils/common';
import { AppRoute } from ':cloud/utils/routes';

import { PaymentMethodIntake } from '../billing/PaymentMethodIntake';
import { ClipboardIcon } from '../sharedcomponents';

import { BaseAppchainStatusEnum, BaseAppchainTypeEnum, BaseNetworkEnum } from './constants';

type BaseL3DeploymentModalProps = {
  onClose: () => void;
  isVisible: boolean;
  brandColor?: string;
  chain?: BaseL3Chain;
};
export function DeploymentModal({
  brandColor,
  onClose,
  isVisible,
  chain,
}: BaseL3DeploymentModalProps) {
  const { selectedProject } = useProject();
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { paymentMethod } = useGetPaymentMethod(orgId);

  const [
    addPaymentMethodModalVisible,
    { toggleOn: showAddPaymentMethodModal, toggleOff: hideAddPaymentMethodModal },
  ] = useToggler();

  const toast = useToast();
  const queryClient = useQueryClient();

  const updateBaseL3Mutation = useUpdateBaseL3({
    orgId,
    projectId: selectedProject?.id,
  });

  const handleStripeSuccess = useCallback(async () => {
    toast.show('Payment method successfully added.', {
      variant: 'positive',
    });
    await queryClient.invalidateQueries({ queryKey: [QueryKey.billing, 'paymentMethod', orgId] });
    await queryClient.invalidateQueries({ queryKey: [QueryKey.accountCredits, orgId] });
    hideAddPaymentMethodModal();
  }, [toast, queryClient, orgId, hideAddPaymentMethodModal]);

  // TODO: add bank account icon
  const cardMedia = useMemo(
    () => getCardMedia(paymentMethod?.card?.network, <Icon name="card" size="m" />),
    [paymentMethod],
  );

  const handleStripeError = useCallback(
    (intent) => {
      if (intent.last_setup_error?.message) {
        toast.show(intent.last_setup_error.message, {
          variant: 'negative',
        });
      }
    },
    [toast],
  );

  const handleStripePaymentMethodError = useCallback(
    (errorMessage: string | undefined) => {
      if (errorMessage) {
        toast.show(errorMessage, {
          variant: 'negative',
        });
      }
    },
    [toast],
  );

  const handleSubmit = useCallback(() => {
    updateBaseL3Mutation.mutate({
      uuid: chain?.uuid,
      status: BaseAppchainStatusEnum.Running,
      chainId: chain?.id,
    });
    onClose();
  }, [chain?.id, chain?.uuid, onClose, updateBaseL3Mutation]);

  const paymentMethodDetails = useMemo(() => {
    if (paymentMethod?.card?.lastFour) {
      return paymentMethod?.card?.lastFour;
    }

    if (paymentMethod?.type === 'sepa_debit') {
      return paymentMethod?.sepaDebit?.lastFour;
    }

    return '';
  }, [paymentMethod]);

  const isDisabled = !paymentMethod;

  const isTestnet = chain?.network === BaseNetworkEnum.Testnet;
  const rollupName = useMemo(() => {
    return chain?.name;
  }, [chain?.name]);
  const tokenAddress = useMemo(() => {
    return chain?.customizations?.customGasToken;
  }, [chain?.customizations?.customGasToken]);

  const cost = useMemo(() => {
    if (chain?.type === BaseAppchainTypeEnum.SharedTestnet) {
      return '$1 / month';
    }
    // TODO: revisit when pricing is locked
    if (!isTestnet) {
      return 'Contact us';
    }
    return '$50 / month';
  }, [chain?.type, isTestnet]);

  return (
    <>
      <Modal
        visible={addPaymentMethodModalVisible}
        onRequestClose={hideAddPaymentMethodModal}
        shouldCloseOnEscPress={false}
      >
        <ModalHeader title="Add payment method" />
        <ModalBody>
          <VStack spacingBottom={5}>
            <PaymentMethodIntake
              onCancel={hideAddPaymentMethodModal}
              returnRoute={AppRoute.Products.BaseL3Deploy}
              parentComponentName="Billing"
              actionsInline
              onSuccess={handleStripeSuccess}
              onRequiresPaymentMethod={handleStripeError}
              onError={handleStripePaymentMethodError}
            />
          </VStack>
        </ModalBody>
      </Modal>
      <Modal visible={isVisible} onRequestClose={onClose} width={450}>
        <ModalHeader title="Review deployment" />
        <ModalBody>
          <VStack gap={2}>
            <VStack gap={2} borderedBottom spacingBottom={2}>
              <HStack alignItems="center" gap={2} spacingBottom={2}>
                <VStack>
                  <TextTitle3 as="h3">{rollupName}</TextTitle3>
                  <TextBody as="p">{brandColor}</TextBody>
                </VStack>
                <Tag colorScheme={isTestnet ? 'green' : 'yellow'}>
                  {isTestnet ? 'Testnet' : 'Mainnet'}
                </Tag>
              </HStack>
              {chain?.addresses?.feeVaultRecipient && (
                <HStack justifyContent="space-between" alignItems="center" gap={2}>
                  <TextBody as="p" color="foregroundMuted">
                    Sequencer funds
                  </TextBody>
                  <HStack alignItems="center" gap={2}>
                    <TextBody as="p">
                      {truncateMiddle(chain?.addresses?.feeVaultRecipient || '', 4, 4, '...')}
                    </TextBody>
                    <ClipboardIcon text={chain?.addresses?.feeVaultRecipient || ''} />
                  </HStack>
                </HStack>
              )}
              {tokenAddress && (
                <HStack justifyContent="space-between" alignItems="center" gap={2}>
                  <TextBody as="p" color="foregroundMuted">
                    Custom gas token
                  </TextBody>
                  <HStack alignItems="center" gap={2}>
                    <TextBody as="p">{truncateMiddle(tokenAddress || '', 4, 4, '...')}</TextBody>
                    <ClipboardIcon text={tokenAddress || ''} />
                  </HStack>
                </HStack>
              )}
            </VStack>

            <HStack justifyContent="space-between">
              <TextBody as="p" color="foregroundMuted">
                Sequencer fees
              </TextBody>
              <TextBody as="p" color="positive">
                100% to you
              </TextBody>
            </HStack>
            <HStack justifyContent="space-between">
              <TextBody as="p" color="foregroundMuted">
                Deployment fee
              </TextBody>
              <TextBody as="p" color="positive">
                Free
              </TextBody>
            </HStack>
            <HStack justifyContent="space-between">
              <TextBody as="p" color="foregroundMuted">
                Plan
              </TextBody>
              <TextBody as="p">{cost}</TextBody>
            </HStack>
            <HStack justifyContent="space-between">
              <TextBody as="p" color="foregroundMuted">
                Pay with
              </TextBody>
              {paymentMethod ? (
                <HStack alignItems="center" gap={1}>
                  {cardMedia}
                  <TextBody as="p">{`****${paymentMethodDetails}`}</TextBody>
                </HStack>
              ) : (
                <Pressable background="transparent" onPress={showAddPaymentMethodModal}>
                  <TextHeadline as="p" color="primary">
                    Add payment method
                  </TextHeadline>
                </Pressable>
              )}
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter
          primaryAction={
            <Button disabled={isDisabled} variant="primary" onPress={handleSubmit}>
              Pay and deploy
            </Button>
          }
        />
      </Modal>
    </>
  );
}
